import styles from './styles.module.scss';

import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board } from '../../../../../../components/Board';
import { NavButton } from '../../../../../../components/NavButton';
import { ProfileBox } from '../../../../../../components/ProfileBox';
import { SelectionList } from '../../../../../../components/SelectionList';
import { SelectionItem } from '../../../../../../components/SelectionListItem';
import { verifyRoutePermissions } from '../../../../../../globalFunctions';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import { User, LoadData, SchoolOptions, School, Error } from '../../../../../../types';
import { LogicManagerSchoolDetail } from '../LogicManagerSchoolDetail';
import { useOptions } from '../../../../../../hooks/useOptions';
import { LoadingContext } from '../../../../../../providers/LoadingProvider';
import { isEqual } from 'lodash';
import { AlertDialog, AlertDialogType } from '../../../../../../components/AlertDialog';
import { MediaContentDAO } from '../../../../../../classes/MediaContentDAO';
import { useSchool } from '../../../../../../hooks/useSchool';
import { SchoolDAO } from '../../../../../../classes/SchoolDAO';

export const LogicManagerSchoolMaintainRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const navigate = useNavigate();

    // Load data
    const options: LoadData = useOptions('school');
    const schools: LoadData = useSchool();
    const [schoolOptions, setSchoolOptions] = useState<SchoolOptions>();
    const [schoolsList, setSchoolsList] = useState<SelectionItem[]>();
    const [loadedSchools, setLoadedSchools] = useState<School[]>();
    const [selectedSchool, setSelectedSchool] = useState<School>();

    // Handle changes
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogType>();
    const [originalData, setOriginalData] = useState<School>();
    const { setIsLoadingContent } = useContext(LoadingContext);
    const { setOpenMessage, setMessage } = useContext(MessageContext);

    const save = async () => {
        if (selectedSchool) {
            try {
                setIsLoadingContent(true);

                const isNew = selectedSchool.id === 0;
                const result = await SchoolDAO.save(userAuth.token, navigate, selectedSchool);

                if (
                    result &&
                    typeof result === 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    const currentSchool = Object.assign({}, result.school);
                    currentSchool.is_active = true;

                    if (selectedSchool.image?.file) {
                        const mediaContent = await MediaContentDAO.store(
                            userAuth.token,
                            navigate,
                            currentSchool.id,
                            selectedSchool.image,
                            'school'
                        );

                        if (
                            mediaContent &&
                            typeof mediaContent === 'object' &&
                            !Object.keys(mediaContent).includes('error')
                        ) {
                            currentSchool.image = mediaContent.image;
                        } else {
                            throw mediaContent as Error;
                        }
                    }

                    setSelectedSchool({ ...currentSchool });
                    setOriginalData({ ...currentSchool });
                    updateSchoolsList({ ...currentSchool }, false, isNew);
                    updateLoadedSchools({ ...currentSchool }, false, isNew);

                    setMessage({
                        type: 'success',
                        content: 'Escola salva com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const reset = () => {
        if (originalData) {
            setSelectedSchool({ ...originalData });
        }
    };

    const restore = async () => {
        if (selectedSchool) {
            try {
                setIsLoadingContent(true);

                const result = await SchoolDAO.restore(userAuth.token, navigate, selectedSchool.id);

                if (result) {
                    const currentSchool = Object.assign({}, originalData);
                    currentSchool.is_active = true;

                    setSelectedSchool({ ...currentSchool });
                    setOriginalData({ ...currentSchool });
                    updateSchoolsList({ ...currentSchool });
                    updateLoadedSchools({ ...currentSchool });

                    setMessage({
                        type: 'success',
                        content: 'Escola reativada com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const remove = async () => {
        if (selectedSchool) {
            try {
                const response = await SchoolDAO.delete(
                    userAuth.token,
                    navigate,
                    selectedSchool.id
                );

                if (response) {
                    const currentSchool = Object.assign({}, originalData);
                    currentSchool.is_active = false;

                    setSelectedSchool({ ...currentSchool });
                    setOriginalData({ ...currentSchool });
                    updateSchoolsList({ ...currentSchool });
                    updateLoadedSchools({ ...currentSchool });

                    setMessage({
                        type: 'success',
                        content: 'Escola desativada com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw response as any;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    };

    const add = () => {
        const newSchool: School = {
            id: 0,
            image: null,
            name: '',
            slug: '',
            chain: null,
            type: {
                id: 0,
                internal_type: '',
                name: ''
            },
            scope: null,
            country: '',
            state: '',
            city: '',
            moodle_server: '',
            has_moodle_server: 0,
            is_active: true,
            is_yours: false
        };

        setSelectedSchool({ ...newSchool });
        setOriginalData({ ...newSchool });
    };

    const onAdd = () => {
        if (isEqual(selectedSchool, originalData)) {
            add();
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const onSave = async () => {
        if (selectedSchool) {
            try {
                setIsLoadingContent(true);

                if (validate(selectedSchool)) {
                    if (!isEqual(selectedSchool, originalData)) {
                        await save();
                    } else {
                        setMessage({
                            type: 'info',
                            content: 'Sem alterações para salvar'
                        });
                        setOpenMessage(true);
                    }
                }
            } catch (error: any) {
                const e = error as Error;

                setMessage({
                    type: 'error',
                    content: e.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const onRestore = () => {
        setAlertDialogProps({
            title: 'Confirmar ativação',
            message: 'Deseja reativar esta escola?',
            options: [
                {
                    text: 'Reativar',
                    action: async () => await restore(),
                    type: 'warning'
                }
            ]
        });
        setOpenAlert(true);
    };

    const onDelete = () => {
        setAlertDialogProps({
            title: 'Confirmar desativação',
            message: 'Deseja realmente desativar esta escola?',
            options: [
                {
                    text: 'Desativar',
                    action: async () => await remove(),
                    type: 'error'
                }
            ]
        });
        setOpenAlert(true);
    };

    const validate = (school: School) => {
        if (school.name.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo nome'
            });
            setOpenMessage(true);

            return false;
        }

        if (school.slug.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo sigla'
            });
            setOpenMessage(true);

            return false;
        }

        if (school.type.id === 0) {
            setMessage({
                type: 'warning',
                content: 'Selecione o tipo de escola'
            });
            setOpenMessage(true);

            return false;
        }

        if (school.country.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Selecione o país da escola'
            });
            setOpenMessage(true);

            return false;
        }

        if (school.country.trim() === 'Brasil' && school.state.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Selecione o estado da escola'
            });
            setOpenMessage(true);

            return false;
        }

        if (school.country.trim() === 'Brasil' && school.city.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Selecione a cidade da escola'
            });
            setOpenMessage(true);

            return false;
        }

        return true;
    };

    const onSelectSchool = (id: any) => {
        return new Promise<boolean>((resolve, reject) => {
            if (loadedSchools) {
                loadedSchools.forEach((school: School) => {
                    if (school.id === id) {
                        if (
                            selectedSchool &&
                            originalData &&
                            school.id !== originalData.id &&
                            !isEqual(selectedSchool, originalData)
                        ) {
                            setAlertDialogProps({
                                title: 'Confirmar alterações',
                                message: 'Deseja salvar as alterações?',
                                options: [
                                    {
                                        text: 'Desfazer',
                                        action: reset,
                                        type: 'error'
                                    },
                                    {
                                        text: 'Salvar',
                                        action: async () => await save(),
                                        type: 'success'
                                    }
                                ]
                            });
                            setOpenAlert(true);

                            resolve(false);
                        } else {
                            const currentSelectedSchool = Object.assign({}, school);
                            setSelectedSchool({ ...currentSelectedSchool });
                            setOriginalData({ ...currentSelectedSchool });

                            resolve(true);
                        }
                    }
                });
            }

            reject(false);
        });
    };

    const goBackwards = () => {
        if (isEqual(selectedSchool, originalData)) {
            navigate(`/manager`);
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const updateSchoolsList = (
        currentSchool: School,
        wasRemoved: boolean = false,
        isNew: boolean = false
    ) => {
        if (schoolsList) {
            const schoolsData: SelectionItem[] = [];

            if (isNew) {
                schoolsData.push({
                    id: currentSchool.id,
                    name: currentSchool.slug,
                    image: currentSchool.image?.url,
                    description: currentSchool.is_yours
                        ? 'Você está vinculado'
                        : `${currentSchool.country}${
                              currentSchool.state !== '' ? `/${currentSchool.state}` : ''
                          }`
                });
            }

            schoolsList.forEach((school: SelectionItem) => {
                if (school.id === currentSchool.id) {
                    if (!wasRemoved) {
                        schoolsData.push({
                            id: currentSchool.id,
                            name: currentSchool.slug,
                            image: currentSchool.image?.url,
                            description: currentSchool.is_yours
                                ? 'Você está vinculado'
                                : `${currentSchool.country}${
                                      currentSchool.state !== '' ? `/${currentSchool.state}` : ''
                                  }`
                        });
                    }
                } else {
                    schoolsData.push(school);
                }
            });

            setSchoolsList(schoolsData);
        }
    };

    const updateLoadedSchools = (
        currentSchool: School,
        wasRemoved: boolean = false,
        isNew: boolean = false
    ) => {
        if (loadedSchools) {
            const schoolsData: School[] = [];

            if (isNew) {
                schoolsData.push(currentSchool);
            }

            loadedSchools.forEach((school: School) => {
                if (school.id === currentSchool.id) {
                    if (!wasRemoved) {
                        schoolsData.push(currentSchool);
                    }
                } else {
                    schoolsData.push(school);
                }
            });

            setLoadedSchools(schoolsData);
        }
    };

    useEffect(() => {
        if (!schools.isLoading) {
            const data = schools.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                const schoolsData: SelectionItem[] = [];

                schools.data.forEach((school: School) => {
                    schoolsData.push({
                        id: school.id,
                        name: school.slug,
                        image: school.image?.url,
                        description: school.is_yours
                            ? 'Você está vinculado'
                            : `${school.country}${school.state !== '' ? `/${school.state}` : ''}`
                    });
                });

                setSchoolsList(schoolsData);
                setLoadedSchools(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [schools.isLoading]);

    useEffect(() => {
        if (!options.isLoading) {
            const data = options.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                setSchoolOptions(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [options.isLoading]);

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.classes'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
            {userAuth.token && schoolsList && schoolOptions && (
                <>
                    <ProfileBox user={userAuth} />
                    <Board wrapperClass={styles.board}>
                        <div className={styles.boardWrapper}>
                            <h1 className={styles.title}>Instituições</h1>

                            <div className={styles.editContainer}>
                                <SelectionList
                                    items={schoolsList}
                                    defaultSelectedItem={
                                        selectedSchool &&
                                        ({
                                            id: selectedSchool.id,
                                            name: selectedSchool.slug,
                                            image: selectedSchool.image?.url,
                                            description: selectedSchool.is_yours
                                                ? 'Você está vinculado'
                                                : `${selectedSchool.country}${
                                                      selectedSchool.state !== ''
                                                          ? `/${selectedSchool.state}`
                                                          : ''
                                                  }`
                                        } as SelectionItem)
                                    }
                                    onSelectItem={onSelectSchool}
                                    searchPlaceholder='Pesquisar'
                                    wrapperClass={styles.list}
                                />
                                <LogicManagerSchoolDetail
                                    school={selectedSchool}
                                    setSchool={setSelectedSchool}
                                    options={schoolOptions}
                                    onSave={onSave}
                                    onAdd={onAdd}
                                    onRestore={onRestore}
                                    onDelete={onDelete}
                                    userAuth={userAuth}
                                />
                            </div>
                        </div>
                    </Board>
                </>
            )}
            {alertDialogProps && (
                <AlertDialog
                    setOpen={setOpenAlert}
                    open={openAlert}
                    title={alertDialogProps.title}
                    message={alertDialogProps.message}
                    options={alertDialogProps.options}
                />
            )}
        </div>
    );
};
